import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { useParams } from 'react-router';
import { ConfiguratorOptionDto } from '@/services/configurator';
import { useConfiguratorMutations } from '../ConfiguratorMutationContext';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { DependencyWizardModal } from '../DependencyWizardModal/DependencyWizardModal';
import { ImmersiveDependencyWizardModal } from '../ImmersiveDependencyWizard/ImmersiveDependencyWizardModal';
import { isDeadlineInPast } from '@/utils/date';

export interface OptionSelectionState {
    /** This option is the currently selected one in its group. */
    selected: boolean;
    /** A selection mutation is in flight. */
    isSelecting: boolean;
    /** The option cannot be selected by a normal click (style/deadline/lock gates). */
    disabled: boolean;
    /** Clicking opens the dependency wizard rather than selecting directly. */
    triggersWizard: boolean;
    /** Tooltip to surface for the current state (disabled reason or wizard hint), if any. */
    tooltip: React.ReactNode | null;
    /**
     * Select the option (or open the dependency wizard). No-op when disabled. Pass a quantity to
     * commit a buyer-entered amount; omitted, the group's current quantity is re-sent unchanged.
     */
    select: (quantity?: number) => void;
    /** Clear this option's selection (deselect its group). No-op when disabled. */
    deselect: () => void;
    /** The dependency wizard modal, rendered when open (else null). Place it in the tree. */
    wizard: React.ReactNode;
    /** The group takes a buyer-entered quantity, and this view may edit it. */
    quantityEditable: boolean;
    /** Committed whole-number quantity for the group — the stepper's value. */
    currentQuantity: number;
    /** The quantity stepper must not accept input right now (in-flight, or a locked group). */
    quantityDisabled: boolean;
}

/**
 * Shared selection logic for a configurator option — the gating, the dependency wizard, and the
 * mutation call. Consumed by both the standalone `OptionSelectButton` and the compact (click-the-
 * image) showroom card so the rules stay in one place.
 */
export const useOptionSelection = (option: ConfiguratorOptionDto): OptionSelectionState => {
    const { viewOnly, configuratorData, projectData, selectedDeadlineId, isShowroom, immersive } =
        useOptionConfigurator();
    const { selectOption, clearSelection, isSelecting } = useConfiguratorMutations();
    const { roomId } = useParams<{ roomId: string }>();
    const [wizardOpen, setWizardOpen] = useState(false);

    const { disabledByDependencies, disabledByPresets, disabledByManualSelection } = option.optionStatus;
    const group = (configuratorData?.groups ?? []).find((x) => x.roomId === +roomId && x.id === option.optionGroupId);

    // In the multi-deadline preview the URL deadline is vestigial (the view shows every deadline at
    // once), so gate and persist against the group's OWN deadline. In the standard single-deadline
    // flow these are identical, so this is a no-op there.
    const groupDeadlineId = group?.deadlineId ?? selectedDeadlineId;
    const selectedDeadline = projectData?.deadlines.find((d) => d.id === groupDeadlineId);
    const deadlinePassed = !!selectedDeadline && isDeadlineInPast(selectedDeadline.deadline);

    // Determine the interaction in the same precedence the standalone select button used.
    let kind: 'select' | 'wizard' | 'disabled' = 'select';
    let tooltip: React.ReactNode = null;

    if (viewOnly) {
        kind = 'disabled';
        tooltip = <Trans>Selections are disabled in this view</Trans>;
    } else if (deadlinePassed && !isShowroom) {
        // Deadlines are irrelevant in the showroom (anonymous preview): never block on an expired deadline.
        kind = 'disabled';
        tooltip = <Trans>The deadline for this option has expired</Trans>;
    } else if (disabledByManualSelection) {
        kind = 'disabled';
        tooltip = <Trans>Disabled by manual selection</Trans>;
    } else if (disabledByDependencies) {
        kind = 'wizard';
        tooltip = <Trans>By selecting this option, you will need to update dependent options.</Trans>;
    } else if (disabledByPresets) {
        kind = 'disabled';
        tooltip = <Trans>Disabled by style selection</Trans>;
    } else if (group?.isLocked && group.lockedByForceSelectionPreset) {
        kind = 'disabled';
        tooltip = <Trans>You need to pick a style before selecting options</Trans>;
    } else if (group?.isLocked && !isShowroom) {
        // Outside the force-selection-preset case, `isLocked` is driven by an expired deadline (or a
        // sent selection) — neither applies in the showroom, so don't block selection there.
        kind = 'disabled';
        tooltip = <Trans>This option group is locked</Trans>;
    }

    const allowManualQuantity = !!group?.allowManualQuantity;
    // The group quantity is the committed value (the engine echoes the saved manual quantity, or the
    // pricing-field/default of 1). Manual entry is whole-number, so round the prefill.
    const currentQuantity = group?.quantity != null ? Math.max(1, Math.round(group.quantity)) : 1;

    const select = (quantity?: number) => {
        if (kind === 'disabled') return;
        if (kind === 'wizard') {
            setWizardOpen(true);
            return;
        }
        // Pass the active room explicitly: some mutation providers (e.g. ShowroomMutationProvider)
        // sit above the room route and can't read roomId from useParams themselves.
        selectOption({
            option,
            customFieldData: option.customFieldData ?? null,
            roomId: roomId && roomId !== '0' ? Number(roomId) : null,
            deadlineId: groupDeadlineId,
            quantity: allowManualQuantity ? (quantity ?? currentQuantity) : null,
        });
    };

    // Clicking the already-selected option clears the group's selection. Respect the same hard
    // gates that block selecting (view-only, expired deadline, locked group); the dependency-wizard
    // gate doesn't apply to an option that is already the current pick.
    const deselect = () => {
        if (kind === 'disabled') return;
        clearSelection({
            groupId: option.optionGroupId,
            roomId: roomId && roomId !== '0' ? Number(roomId) : null,
            deadlineId: groupDeadlineId,
        });
    };

    // The immersive configurator uses a compact, viewport-fitting variant that matches its option
    // menu; every other buyer view keeps the plain dialog.
    const wizard =
        wizardOpen && group ? (
            immersive ? (
                <ImmersiveDependencyWizardModal triggerOption={option} onClose={() => setWizardOpen(false)} />
            ) : (
                <DependencyWizardModal triggerOption={option} onClose={() => setWizardOpen(false)} />
            )
        ) : null;

    return {
        selected: !!option.selected,
        isSelecting,
        disabled: kind === 'disabled',
        triggersWizard: kind === 'wizard',
        tooltip,
        select,
        deselect,
        wizard,
        quantityEditable: allowManualQuantity && !viewOnly,
        currentQuantity,
        // `isLocked` outside the force-selection case means an expired deadline or a sent selection —
        // neither applies in the showroom, so the stepper stays live there.
        quantityDisabled: isSelecting || (!!group?.isLocked && !isShowroom),
    };
};
